<template>
  <div class="container-fluid">
    <page-header
      :page="page"
      has-return
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    >
      <template v-slot:customActions>
        <ActionButton
          v-if="isEditMode"
          text="Eliminar"
          class="mb-1"
          variant="outline-danger"
          i="fas fa-times-circle"
          @click="deleteSysParam"
        />
        <ActionButton
          class="mb-1 ml-1"
          text="Guardar"
          variant="success"
          i="fas fa-save"
          @click="saveChanges"
        />
      </template>
    </page-header>

    <ValidationObserver ref="createEditForm">
      <create-edit-form
        ref="formInformation"
        v-model="model"
        :academic-years="academicYears"
        :type-codes="typeCodes"
        :data-type-codes="dataTypeCodes"
        :resources="resources"
        @refresh-data-type-codes="getDataTypeCodes"
        @hide-bool="hideBool"
      />
    </ValidationObserver>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import CreateEditForm from './components/CreateEditForm';
import { ValidationObserver } from 'vee-validate';

import alertMixin from '@/mixin/alert.mixin';
import objectToFormData from 'object-to-formdata';
import {
  getAcademicYears,
  getTypeCodes,
  getDataTypeCodes,
  getSysParamById,
  saveSysParam,
  updateSysParam,
  deleteSysParam,
} from '@/modules/system-parameter-managment/api';
import resourcesService from '@/services/resources.js';

import dataTypeCodes from '@/utils/constants/sysParamCodeTypes/dataTypeCodes';
import typeCodes from '@/utils/constants/sysParamCodeTypes/typeCodes';

const SYS_PARAMETER_MANAGEMENT_ROUTE = 'systemParameter';

export default {
  name: 'AddEditSystemParameter',
  mixins: [alertMixin],
  components: {
    PageHeader,
    ActionButton,
    CreateEditForm,
    ValidationObserver,
  },
  data() {
    return {
      model: {},
      academicYears: [],
      typeCodes: [],
      dataTypeCodes: [],
      resources: [],
    };
  },
  computed: {
    page() {
      const page = {
        icon: 'fad fa-calculator',
        title: 'Manejo de Parámetros de Sistema',
        routes: [
          'Inicio',
          'Operaciones',
          'Manejo de Parámetros de Sistema',
          'Crear Parámetros de Sistema',
        ],
      };

      if (this.isEditMode) {
        page.routes = [
          'Inicio',
          'Operaciones',
          'Manejo de Parámetros de Sistema',
          'Editar Parámetros de Sistema',
        ];
      }

      return page;
    },
    isEditMode() {
      return this.$route.params.id ? true : false;
    },
    isMobile() {
      return this.$store.getters.getIsMobileDensity;
    },
  },
  watch: {},
  methods: {
    async saveChanges() {
      const valid = await this.$refs.createEditForm.validate();

      if (valid) {
        const paramsToSend = {
          ...this.model,
          academicYearId: this.model.academicYear?.id,
          typeCodeId: this.model.typeCodeId?.id,
          dataTypeCodeId: this.model.dataTypeCodeId?.id,
        };

        let formData = objectToFormData(paramsToSend, { indices: true });
        if (this.isEditMode) {
          await updateSysParam(formData);
          this.goBack();
        } else {
          await saveSysParam(formData);
          this.goBack();
        }

        this.ShowToast(
          'Proceso Exitoso.',
          'El parámetro de sistema ha sido guardado exitosamente.',
          'success'
        );
      } else {
        this.ShowToast(
          'Alerta',
          'Favor de completar la información solicitada.',
          'error'
        );
        return;
      }
    },
    async deleteSysParam() {
      const sysParamId = this.$route.params.id;

      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea eliminar el parámetro ${this.model.name}?`
      );
      if (answer) {
        deleteSysParam(sysParamId)
          .then(() => {
            this.goBack();
          })
          .catch((err) => {
            if (err && err.response && err.response.data) {
              this.ShowToast('Error', err.response.data.message, 'error');
            } else {
              this.ShowToast('Error', err.message, 'error');
            }
          });
      }
    },
    goBack() {
      this.$router.push({ name: SYS_PARAMETER_MANAGEMENT_ROUTE });
    },
    async getSysParam(id) {
      const sysParam = await getSysParamById(id);

      if (sysParam) {
        this.model = {
          ...sysParam.data,
          academicYear: this.academicYears.find(
            (x) => x.id == sysParam.data.academicYearId
          ),
          typeCodeId: this.typeCodes.find(
            (x) => x.id == sysParam.data.typeCodeId
          ),
          dataTypeCodeId: this.dataTypeCodes.find(
            (x) => x.id == sysParam.data.dataTypeCodeId
          ),
        };
      }
    },
    hideBool() {
      this.dataTypeCodes = this.dataTypeCodes.filter(
        (x) => x.id != dataTypeCodes.BOOL
      );
    },
    // GETS
    async getAcademicYears() {
      await getAcademicYears().then(({ data }) => {
        this.academicYears = data;
      });
    },
    async getTypeCodes() {
      await getTypeCodes().then(({ data }) => {
        this.typeCodes = data;
        this.typeCodes.find((x) => x.id === typeCodes.MULTIPLE_VALUE).name =
          'Valores Multiples';
        this.typeCodes.find((x) => x.id === typeCodes.STATIC).name = 'Estatico';
        this.typeCodes.find((x) => x.id === typeCodes.RANGE).name = 'Rango';
      });
    },
    async getDataTypeCodes() {
      await getDataTypeCodes().then(({ data }) => {
        this.dataTypeCodes = data;
      });
    },
    async loadResources() {
      this.resources = await resourcesService.LoadValidationMessages([
        '25001',
        '25002',
        '25003',
        '25004',
        '25005',
        '25006',
        '25007',
        '25008',
        '25009',
        '25010',
        '25011',
        '25012',
        '25013',
        '25015',
      ]);
    },
  },
  async mounted() {
    await this.getAcademicYears();
    await this.loadResources();
    await this.getTypeCodes();
    await this.getDataTypeCodes();

    if (this.isEditMode) {
      await this.getSysParam(this.$route.params.id);
    }
  },
};
</script>
<style scoped>
::v-deep .width-container {
  width: 100% !important;
}
@media only screen and (max-width: 375px) {
  ::v-deep .tag-badge-title-info {
    font-size: 12px !important;
  }
}
</style>
